import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { paths } from '@/app/router/paths';
import { Button, KpiCard, Skeleton } from '@/design-system';
import {
    dashboardApi,
    formatMoney,
    orderAccessFor,
    orderApi,
    orderKeys,
    organisationApi,
    type DashboardSignal,
    type DashboardSummary,
} from '@/entities/order';
import { tenantApi, tenantKeys } from '@/entities/tenant';
import { hasRole, useSessionStore } from '@/shared/api';
import { formatNumber } from '@/shared/lib';
import { ErrorState, Screen, ScreenBody, ScreenHeader } from '@/shared/ui';

import { AwaitingCompletionCard } from './components/AwaitingCompletionCard';
import { FieldStartCard } from './components/FieldStartCard';
import { GettingStartedCard } from './components/GettingStartedCard';
import { OccupancyCard } from './components/OccupancyCard';
import { SignalCard } from './components/SignalCard';
import { dashboardCopy } from './copy';
import { nextStepFor, type NextStep } from './getting-started';

const NEW_INSPECTION = `${paths.inspections}?nieuw=1`;

/** Waar elke volgende stap gezet wordt. */
const STEP_TARGETS: Record<NextStep, string> = {
    company_details: paths.settings,
    house_style: paths.settingsTab('branding'),
    first_inspection: NEW_INSPECTION,
    choose_plan: paths.choosePlan,
};

/** Hoogstens drie signalen: meer is een lijst, geen signaal. */
const MAX_SIGNALS = 3;

/**
 * Scherm 2 - Dashboard (in het prototype: Dashboard bedrijf).
 *
 * Hoogstens vier KPI's, en de doorlooptijd van het rapport staat altijd bij de
 * eerste twee: dat is de belofte van het product. Elke kaart eronder draagt een
 * vervolgactie of een wachttijd; een getal zonder vervolg is decoratie
 * (principe 7). Wat de API niet kan meten staat er als "onbekend", niet als 0.
 *
 * **Een bedrijf zonder keuringen** krijgt geen rij nullen en lege kaarten, maar
 * één kaart die zegt waar je begint (`GettingStartedCard`, voor een inspecteur
 * `FieldStartCard`). De KPI's en kaarten komen pas als er een keuring is. "Geen
 * keuringen" is `meta.total` van `GET /orders` - dezelfde telling als de rail,
 * dus geen extra verzoek. Zolang die telling er niet is, of mislukt, geldt het
 * gewone dashboard: liever cijfers dan een onterechte welkomstkaart.
 *
 * **Aan de slag** voegt de proef en de eerste stappen samen tot één blok met één
 * knop: de volgende stap (`nextStepFor`). Alleen voor de eigenaar.
 */
export function DashboardPage() {
    const navigate = useNavigate();
    const client = useQueryClient();
    const session = useSessionStore((state) => state.session);
    const access = orderAccessFor(session);
    const isOwner = hasRole(session, ['owner']);

    const summary = useQuery({
        queryKey: orderKeys.dashboard(),
        queryFn: () => dashboardApi.summary(),
    });
    const occupancy = useQuery({
        queryKey: orderKeys.occupancy('deze-week'),
        queryFn: () => dashboardApi.occupancy(),
    });
    const signals = useQuery({
        queryKey: orderKeys.signals(),
        queryFn: () => dashboardApi.signals(),
    });
    // Of rapporten vanzelf de deur uit gaan is een instelling van het bedrijf,
    // geen dashboardcijfer: één bron van waarheid.
    const settings = useQuery({
        queryKey: orderKeys.settings(),
        queryFn: () => organisationApi.settings(),
        enabled: isOwner,
    });
    // De proef en de eerste stappen zijn voor de eigenaar: hij kiest het
    // abonnement en vult de bedrijfsgegevens in.
    const tenant = useQuery({
        queryKey: tenantKeys.current,
        queryFn: () => tenantApi.current(),
        enabled: isOwner,
    });
    const onboarding = useQuery({
        queryKey: orderKeys.onboarding(),
        queryFn: () => dashboardApi.onboarding(),
        enabled: isOwner,
    });
    const hideOnboarding = useMutation({
        mutationFn: () => dashboardApi.dismissOnboarding(),
        onSuccess: (next) => client.setQueryData(orderKeys.onboarding(), next),
    });
    // Dezelfde sleutel als de teller in de rail: geen tweede verzoek.
    const total = useQuery({
        queryKey: orderKeys.total(),
        queryFn: () => orderApi.total(),
        staleTime: 5 * 60 * 1_000,
    });

    const trial = isOwner ? (tenant.data?.trial ?? null) : null;
    const steps = isOwner && onboarding.data && !onboarding.data.completed ? onboarding.data : null;
    const empty = total.data === 0;
    const next = isOwner ? nextStepFor({ trial, onboarding: steps, hasInspections: !empty }) : null;

    const data = summary.data;

    function follow(signal: DashboardSignal) {
        switch (signal.target) {
            case 'report':
                navigate(signal.orderUuid ? paths.report(signal.orderUuid) : paths.inspections);
                break;
            case 'billing':
                navigate(paths.billing);
                break;
            case 'plan':
                navigate(paths.choosePlan);
                break;
            case 'buy':
                navigate(paths.buyInspections);
                break;
            case 'planning':
                navigate(paths.planning);
                break;
            default:
                navigate(paths.inspections);
        }
    }

    return (
        <Screen>
            <ScreenHeader
                eyebrow={
                    empty
                        ? dashboardCopy.eyebrowWelcome
                        : data && data.week.number > 0
                          ? dashboardCopy.eyebrow(
                                data.week.number,
                                data.inspectionsThisWeek.value,
                                data.openReports.value,
                            )
                          : dashboardCopy.eyebrowFallback
                }
                title={dashboardCopy.title}
                actions={
                    /* Zonder keuringen staat dezelfde knop als hoofdactie in de startkaart. */
                    access.manage && !(empty && isOwner) ? (
                        <Button
                            variant="primary"
                            size="sm"
                            icon="add"
                            data-tour="new-inspection"
                            onClick={() => navigate(NEW_INSPECTION)}
                        >
                            {dashboardCopy.newInspection}
                        </Button>
                    ) : null
                }
            />

            <ScreenBody>
                {empty ? (
                    <div className="max-w-[760px]">
                        {next ? (
                            <GettingStartedCard
                                next={next}
                                trial={trial}
                                onboarding={steps}
                                alone
                                hiding={hideOnboarding.isPending}
                                onAct={(step) => navigate(STEP_TARGETS[step])}
                                onHide={() => hideOnboarding.mutate()}
                            />
                        ) : (
                            <FieldStartCard
                                field={access.field}
                                onOpen={() =>
                                    navigate(access.field ? paths.myDay : paths.inspections)
                                }
                            />
                        )}
                    </div>
                ) : (
                    <>
                        {summary.isError ? (
                            <ErrorState
                                error={summary.error}
                                title={dashboardCopy.errorTitle}
                                onRetry={() => void summary.refetch()}
                            />
                        ) : null}

                        {next ? (
                            <div className="mb-[18px]">
                                <GettingStartedCard
                                    next={next}
                                    trial={trial}
                                    onboarding={steps}
                                    alone={false}
                                    hiding={hideOnboarding.isPending}
                                    onAct={(step) => navigate(STEP_TARGETS[step])}
                                    onHide={() => hideOnboarding.mutate()}
                                />
                            </div>
                        ) : null}

                        <section
                            aria-label={dashboardCopy.kpiSectionLabel}
                            className="mb-[18px] grid gap-[14px] [grid-template-columns:repeat(auto-fit,minmax(182px,1fr))]"
                        >
                            {data ? (
                                <Kpis data={data} />
                            ) : (
                                Array.from({ length: 4 }, (_, index) => (
                                    <Skeleton
                                        key={index}
                                        height={96}
                                        radius={10}
                                        pulse
                                        label={index === 0 ? dashboardCopy.loading : undefined}
                                    />
                                ))
                            )}
                        </section>

                        <div className="grid items-start gap-[14px] [grid-template-columns:repeat(auto-fit,minmax(330px,1fr))]">
                            <AwaitingCompletionCard
                                rows={data?.awaitingCompletion ?? []}
                                autoSend={settings.data?.autoSendReport ?? null}
                                onOpen={(row) => navigate(paths.report(row.orderUuid))}
                                onOpenAll={() => navigate(paths.inspections)}
                            />

                            <div className="flex flex-col gap-[14px]">
                                <OccupancyCard
                                    rows={occupancy.data ?? []}
                                    onOpenPlanning={
                                        access.assign ? () => navigate(paths.planning) : undefined
                                    }
                                />
                                <SignalCard
                                    signals={(signals.data ?? []).slice(0, MAX_SIGNALS)}
                                    onFollow={follow}
                                />
                            </div>
                        </div>
                    </>
                )}
            </ScreenBody>
        </Screen>
    );
}

function Kpis({ data }: { data: DashboardSummary }) {
    const turnaround = data.reportTurnaround;
    const measured = turnaround.hours !== null && turnaround.minutes !== null;

    return (
        <>
            <KpiCard
                data-kpi="volume"
                label={dashboardCopy.kpiVolume}
                value={formatNumber(data.inspectionsThisWeek.value)}
                delta={data.inspectionsThisWeek.delta ?? undefined}
                deltaToneOverride="accent"
            />
            <KpiCard
                data-kpi="turnaround"
                label={dashboardCopy.kpiTurnaround}
                value={measured ? turnaround.hours : dashboardCopy.unknown}
                unit={measured ? dashboardCopy.turnaroundUnit(turnaround.minutes ?? 0) : undefined}
                delta={turnaround.delta ?? (measured ? undefined : dashboardCopy.turnaroundNoData)}
                deltaToneOverride={turnaround.delta ? 'accent' : 'neutral'}
            />
            <KpiCard
                data-kpi="open-reports"
                label={dashboardCopy.kpiOpenReports}
                value={formatNumber(data.openReports.value)}
                tone={data.openReports.value > 0 ? 'warn' : 'neutral'}
                delta={dashboardCopy.waitingOver24(data.openReports.waitingOver24h)}
                deltaToneOverride="neutral"
            />
            {data.toInvoice ? (
                <KpiCard
                    data-kpi="to-invoice"
                    label={dashboardCopy.kpiToInvoice}
                    value={formatMoney(data.toInvoice.amount)}
                    delta={dashboardCopy.orderCount(data.toInvoice.orders)}
                />
            ) : null}
        </>
    );
}
